import type { ReactNode } from 'react';
import ExpectedLogo from '@/assets/logos/expected-full.svg?react';
import { cn } from '@/lib/utils';

export const MARKETING_SITE_URL = 'https://expectedhealthcare.com';
export const FIND_A_CLINIC_URL = `${MARKETING_SITE_URL}/find-a-clinic/`;

interface PatientPortalLayoutProps {
  children: ReactNode;
  /** The dashboard's width; without it, one narrow column. */
  wide?: boolean;
  /** Present once a patient is signed in: renders Sign out. */
  onSignOut?: () => void;
  signOutError?: string | null;
  /**
   * Framed by the marketing site's sign-in modal: no header (the modal has
   * the site's logo and its own close), no full-height ground, the panel's
   * white behind the content.
   */
  embedded?: boolean;
}

/**
 * The patient pages' frame on the patient kit: a small branded header that
 * links back to the marketing site, no staff chrome.
 * Nothing here carries UNMASK_PROPS: a patient's screen is recorded masked,
 * labels included (patientReplayMasking.test.tsx).
 */
export function PatientPortalLayout({
  children,
  wide = false,
  onSignOut,
  signOutError,
  embedded = false,
}: PatientPortalLayoutProps) {
  const column = cn(
    'mx-auto w-full px-5',
    wide ? 'max-w-[1240px] max-md:px-4' : 'max-w-xl'
  );
  if (embedded) {
    return (
      <div className="flex flex-col bg-white font-patient-body text-patient-ink [color-scheme:light]">
        <main className={cn(column, 'flex flex-1 flex-col pt-4 pb-6')}>
          {children}
        </main>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col bg-patient-ground font-patient-body text-patient-ink [color-scheme:light]">
      <header className="border-b border-patient-line bg-white">
        <div
          className={cn(column, 'flex items-center justify-between gap-3 py-3')}
        >
          <a
            href={MARKETING_SITE_URL}
            aria-label="Expected Healthcare"
            className="flex items-center"
          >
            <ExpectedLogo className="h-7 w-auto" />
          </a>
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              className="rounded-md px-2 py-1 font-patient-display text-sm font-semibold text-patient-ink-muted hover:bg-patient-ground focus-visible:ring-[3px] focus-visible:ring-primary/40 focus-visible:outline-none"
            >
              Sign out
            </button>
          ) : null}
        </div>
      </header>
      <main className={cn(column, 'flex flex-1 flex-col pt-8 pb-12')}>
        {signOutError ? (
          <p role="alert" className="text-patient-error mb-6">
            {signOutError}
          </p>
        ) : null}
        {children}
      </main>
    </div>
  );
}
